/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomIconButton, CustomTooltip } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiActions, uiTypes, uiSelectors, mapEnums } from '@opengeoweb/store';
import { MeteoGate } from '@opengeoweb/theme';

interface DataExplorerButtonConnectProps {
  mapId: string;
  source?: uiTypes.Source;
}

export const DataExplorerButtonConnect: React.FC<
  DataExplorerButtonConnectProps
> = ({ mapId, source = 'app' }: DataExplorerButtonConnectProps) => {
  const dispatch = useDispatch();
  const dialogType = uiTypes.DialogTypes.DataExplorer;

  const currentActiveMapId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, dialogType),
  );

  const isOpenInStore = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, dialogType),
  );

  const openDataExplorerDialog = React.useCallback((): void => {
    dispatch(
      uiActions.setActiveMapIdForDialog({
        type: dialogType,
        setOpen: currentActiveMapId !== mapId ? true : !isOpenInStore,
        source,
        mapId,
        origin: mapEnums.MapActionOrigin.map,
      }),
    );
  }, [dispatch, dialogType, currentActiveMapId, mapId, isOpenInStore, source]);

  return (
    <CustomTooltip title="MeteoGate Data Explorer">
      <CustomIconButton
        data-testid="dataExplorerButton"
        isSelected={currentActiveMapId !== mapId ? false : isOpenInStore}
        onClick={openDataExplorerDialog}
        variant="tool"
      >
        <MeteoGate sx={{}} />
      </CustomIconButton>
    </CustomTooltip>
  );
};
